import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  MessageSquare, Send, Loader2, Sparkles, History, Trash2,
  HelpCircle, ChevronRight, Brain, Lightbulb, Zap
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function NaturalLanguageQuery() {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [history, setHistory] = useState([]);
  const [quickFacts, setQuickFacts] = useState(null);
  const inputRef = useRef(null);

  const fetchSuggestions = async () => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/nlq/suggestions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSuggestions(data.suggestions || []);
      }
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchHistory = async () => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/nlq/history?limit=10`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistory(data.history || []);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchQuickFacts = async () => {
    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/nlq/quick-answers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setQuickFacts(data.quick_facts || null);
      }
    } catch (error) {
      console.error('Error fetching quick facts:', error);
    }
  };

  const clearHistory = async () => {
    const token = localStorage.getItem('qapi-token');
    try {
      await fetch(`${API_URL}/api/nlq/history`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setHistory([]);
    } catch (error) {
      console.error('Error clearing history:', error);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setResponse(null);

    const token = localStorage.getItem('qapi-token');
    try {
      const res = await fetch(`${API_URL}/api/nlq/ask`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ query: query.trim() })
      });

      if (res.ok) {
        const data = await res.json();
        setResponse(data);
        fetchHistory(); // Refresh history
      } else {
        setResponse({
          success: false,
          error: 'Failed to process query. Please try again.'
        });
      }
    } catch (error) {
      console.error('Error processing query:', error);
      setResponse({
        success: false,
        error: 'Network error. Please check your connection.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    inputRef.current?.focus();
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSuggestions();
      fetchHistory();
      fetchQuickFacts();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const getConfidenceColor = (confidence) => {
    if (confidence === 'high') return 'bg-emerald-100 text-emerald-700';
    if (confidence === 'medium') return 'bg-amber-100 text-amber-700';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="space-y-6 p-6" data-testid="nlq-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Brain className="h-7 w-7 text-violet-500" />
            Ask About Your Data
          </h1>
          <p className="text-muted-foreground">AI-powered natural language queries for your hospice operations</p>
        </div>
        <Badge className="bg-violet-100 text-violet-700 flex items-center gap-1">
          <Sparkles className="h-3 w-3" />
          Powered by GPT-5.2
        </Badge>
      </div>

      {/* Quick Facts */}
      {quickFacts && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100/50">
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{quickFacts.current_census?.label}</p>
              <p className="text-xl font-bold">{quickFacts.current_census?.value}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50">
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{quickFacts.admissions_this_week?.label}</p>
              <p className="text-xl font-bold text-emerald-600">+{quickFacts.admissions_this_week?.value}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-50 to-amber-100/50">
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{quickFacts.total_nurses?.label}</p>
              <p className="text-xl font-bold">{quickFacts.total_nurses?.value}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-violet-50 to-violet-100/50">
            <CardContent className="p-3">
              <p className="text-xs text-muted-foreground">{quickFacts.avg_caseload?.label}</p>
              <p className="text-xl font-bold">{quickFacts.avg_caseload?.value}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Query Input */}
      <Card>
        <CardContent className="p-4">
          <form onSubmit={handleSubmit} className="flex gap-3">
            <div className="relative flex-1">
              <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask a question... e.g., 'How many admissions this week?'"
                className="pl-10 pr-4 h-12 text-base"
                disabled={loading}
                data-testid="nlq-input"
              />
            </div>
            <Button type="submit" disabled={loading || !query.trim()} className="h-12 px-6" data-testid="nlq-submit">
              {loading ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <Send className="h-5 w-5 mr-2" />
                  Ask
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Response */}
        <div className="lg:col-span-2 space-y-4">
          {response && (
            <Card data-testid="nlq-response">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-yellow-500" />
                    Answer
                  </CardTitle>
                  {response.response?.confidence && (
                    <Badge className={getConfidenceColor(response.response.confidence)}>
                      {response.response.confidence} confidence
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {response.success ? (
                  <div className="space-y-4">
                    {/* Main Answer */}
                    <div className="p-4 bg-gradient-to-br from-violet-50 to-violet-100/30 rounded-lg">
                      <p className="text-lg">{response.response?.answer}</p>
                    </div>

                    {/* Data Points */}
                    {response.response?.data_points?.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold mb-2">Key Data Points</h4>
                        <div className="space-y-1">
                          {response.response.data_points.map((point, idx) => (
                            <div key={idx} className="flex items-center gap-2 text-sm">
                              <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              {point}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Source */}
                    {response.response?.source && (
                      <p className="text-xs text-muted-foreground">
                        Source: {response.response.source}
                      </p>
                    )}

                    {/* Follow-up Suggestions */}
                    {response.response?.follow_up_suggestions?.length > 0 && (
                      <div className="border-t pt-4">
                        <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                          <Lightbulb className="h-4 w-4 text-amber-500" />
                          Related Questions
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {response.response.follow_up_suggestions.map((suggestion, idx) => (
                            <Button
                              key={idx}
                              variant="outline"
                              size="sm"
                              onClick={() => handleSuggestionClick(suggestion)}
                              className="text-xs"
                            >
                              {suggestion}
                            </Button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-red-600">{response.error}</p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Suggestions */}
          {!response && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <HelpCircle className="h-5 w-5 text-blue-500" />
                  Try Asking...
                </CardTitle>
                <CardDescription>Click a question or type your own</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {suggestions.map((suggestion, idx) => (
                    <Button
                      key={idx}
                      variant="outline"
                      className="justify-start h-auto py-3 px-4 text-left"
                      onClick={() => handleSuggestionClick(suggestion.query)}
                    >
                      <div>
                        <p className="text-sm">{suggestion.query}</p>
                        <Badge variant="secondary" className="mt-1 text-xs">
                          {suggestion.category}
                        </Badge>
                      </div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* History */}
        <div>
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <History className="h-5 w-5" />
                  Recent Queries
                </CardTitle>
                {history.length > 0 && (
                  <Button variant="ghost" size="sm" onClick={clearHistory}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="max-h-96 overflow-y-auto">
              {history.length > 0 ? (
                <div className="space-y-3">
                  {history.map((item, idx) => (
                    <div
                      key={idx}
                      className="p-3 rounded-lg border cursor-pointer hover:bg-muted/50 transition-colors"
                      onClick={() => handleSuggestionClick(item.query)}
                    >
                      <p className="text-sm font-medium">{item.query}</p>
                      <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                        {item.response?.answer}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No recent queries
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
